import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { getToken, getAuthUser, isAuthenticated } from "../auth";
import warningImage from "../assets/warningImage.jpeg";
import dieShapeGuide from "../assets/die-shapes-guide.jpg";

const API = "https://api.vyapakprint.com/api";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ADDON_CONFIG = [
  {
    key: "dieShape",
    label: "Die Shape",
    enabledField: "isDieShape",
    listField: "dieShapes",
    selectionKey: "dieShape",
  },
  {
    key: "goldFoil",
    label: "Gold Foil",
    enabledField: "isGoldFoil",
    listField: "goldFoils",
    selectionKey: "goldFoil",
  },
  {
    key: "goldColor",
    label: "Gold Color",
    enabledField: "isGoldColor",
    listField: "goldColors",
    selectionKey: "goldColor",
  },
  {
    key: "spotUV",
    label: "Spot UV",
    enabledField: "isSpotUV",
    listField: "spotUVOptions",
    selectionKey: "spotUV",
  },
  {
    key: "mattelColor",
    label: "Mattel Color",
    enabledField: "isMattelColor",
    listField: "mattelColors",
    selectionKey: "mattelColor",
  },
  {
    key: "binding",
    label: "Binding",
    enabledField: "isBinding",
    listField: "bindings",
    selectionKey: "binding",
  },
  {
    key: "size",
    label: "Size Customization",
    enabledField: "isSize",
    listField: "sizes",
    selectionKey: "size",
  },
  {
    key: "paperType",
    label: "Paper Type",
    enabledField: "isPaperType",
    listField: "paperTypes",
    selectionKey: "paperType",
  },
  {
    key: "flapOpening",
    label: "Flap Opening",
    enabledField: "isFlapOpening",
    listField: "flapOpenings",
    selectionKey: "flapOpening",
  },
  {
    key: "windowCutting",
    label: "Window Cutting",
    enabledField: "isWindowCutting",
    listField: "windowCuttings",
    selectionKey: "windowCutting",
  },
  {
    key: "printing",
    label: "Printing",
    enabledField: "isPrinting",
    listField: "printings",
    selectionKey: "printing",
  },
  {
    key: "laminationType",
    label: "Lamination Type",
    enabledField: "isLaminationType",
    listField: "laminationTypes",
    selectionKey: "laminationType",
  },
  {
    key: "textureType",
    label: "Texture Type",
    enabledField: "isTextureType",
    listField: "textureTypes",
    selectionKey: "textureType",
  },
];

const normalizeAddonOptions = (arr = []) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((item) => {
      if (typeof item === "string") {
        return { name: item, pricePerUnit: 0 };
      }
      return {
        name: String(item?.name || "").trim(),
        pricePerUnit: Number(item?.pricePerUnit || item?.price || 0),
      };
    })
    .filter((item) => item.name);
};

const getSelectedAddonPrice = (options = [], selectedName = "") => {
  const found = options.find((item) => item.name === selectedName);
  return Number(found?.pricePerUnit || 0);
};

const getPriceFromTiers = (tiers = [], quantity = 0) => {
  if (!Array.isArray(tiers) || !tiers.length) return 0;

  const sorted = [...tiers]
    .map((t) => ({
      quantity: Number(t?.quantity || 0),
      price: Number(t?.price || 0),
    }))
    .filter((t) => t.quantity > 0)
    .sort((a, b) => a.quantity - b.quantity);

  if (!sorted.length) return 0;

  let matched = sorted[0];
  for (const tier of sorted) {
    if (quantity >= tier.quantity) matched = tier;
  }

  return Number(matched.price || 0);
};

const getBasePriceFromProduct = (product, user, quantity = 0) => {
  if (!product) return 0;

  if (Array.isArray(product.priceTiers) && product.priceTiers.length > 0) {
    return getPriceFromTiers(product.priceTiers, quantity);
  }

  const pricesObj = product.prices || {};

  const candidateKeys = [
    user?._id,
    user?.id,
    user?.distributionId,
    user?.userType?._id,
    user?.userType,
    user?.role?._id,
    user?.role,
    user?.category?._id,
    user?.category,
    user?.distributionType?._id,
    user?.distributionType,
  ].filter(Boolean);

  for (const key of candidateKeys) {
    if (pricesObj[key] != null && !Number.isNaN(Number(pricesObj[key]))) {
      return Number(pricesObj[key]);
    }
  }

  const firstPrice = Object.values(pricesObj).find(
    (v) => !Number.isNaN(Number(v)),
  );

  return Number(firstPrice || 0);
};

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return Number.isFinite(num)
    ? num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "0.00";
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const createInitialSelections = () =>
  ADDON_CONFIG.reduce((acc, addon) => {
    acc[addon.selectionKey] = "";
    return acc;
  }, {});

const CreateOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const auth = useMemo(() => getAuthUser(), []);
  const token = useMemo(() => getToken(), []);

  const [formData, setFormData] = useState({
    product: "",
    deliveryTime: "",
    firmName: "",
    quantity: "",
    price: "",
    totalPrice: "",
  });

  const [userDetails, setUserDetails] = useState(null);
  const [minimumQuantity, setMinimumQuantity] = useState(0);
  const [wallet, setWallet] = useState(0);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [businessProductFile, setBusinessProductFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [quantityWarning, setQuantityWarning] = useState(false);

  const [loading, setLoading] = useState(true);
  const [productDetails, setProductDetails] = useState(null);
  const [addonOptionsMap, setAddonOptionsMap] = useState({});
  const [selectedAddons, setSelectedAddons] = useState(
    createInitialSelections(),
  );
  const [showWarning, setShowWarning] = useState(false);
  const [showDieShapeModal, setShowDieShapeModal] = useState(false);

  const cleanupFilePreview = useCallback(() => {
    setFilePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const quantityNumber = useMemo(
    () => Math.max(0, parseInt(formData.quantity || "0", 10) || 0),
    [formData.quantity],
  );

  const baseUnitPrice = useMemo(() => {
    return getBasePriceFromProduct(productDetails, userDetails, quantityNumber);
  }, [productDetails, userDetails, quantityNumber]);

  const enabledAddons = useMemo(() => {
    if (!productDetails) return [];

    return ADDON_CONFIG.filter((addon) => {
      const enabled = Boolean(productDetails?.[addon.enabledField]);
      const options = addonOptionsMap?.[addon.listField] || [];
      return enabled && options.length > 0;
    });
  }, [productDetails, addonOptionsMap]);

  const missingRequiredAddons = useMemo(() => {
    return enabledAddons.filter(
      (addon) => !String(selectedAddons?.[addon.selectionKey] || "").trim(),
    );
  }, [enabledAddons, selectedAddons]);

  const isDieShapeEnabled = useMemo(() => {
    return Boolean(productDetails?.isDieShape);
  }, [productDetails]);

  const addonBreakdown = useMemo(() => {
    return enabledAddons
      .map((addon) => {
        const selectedValue = selectedAddons?.[addon.selectionKey] || "";
        const options = addonOptionsMap?.[addon.listField] || [];
        const pricePerUnit = getSelectedAddonPrice(options, selectedValue);

        if (!selectedValue) return null;

        return {
          key: addon.key,
          label: addon.label,
          selected: selectedValue,
          value: selectedValue,
          pricePerUnit,
        };
      })
      .filter(Boolean);
  }, [enabledAddons, selectedAddons, addonOptionsMap]);

  const addonUnitPrice = useMemo(() => {
    return addonBreakdown.reduce(
      (sum, item) => sum + safeNumber(item.pricePerUnit, 0),
      0,
    );
  }, [addonBreakdown]);

  const finalUnitPrice = useMemo(
    () => safeNumber(baseUnitPrice, 0) + safeNumber(addonUnitPrice, 0),
    [baseUnitPrice, addonUnitPrice],
  );

  const totalPrice = useMemo(
    () => safeNumber(finalUnitPrice, 0) * safeNumber(quantityNumber, 0),
    [finalUnitPrice, quantityNumber],
  );

  const calculationSummary = useMemo(() => {
    return {
      basePricePerUnit: safeNumber(baseUnitPrice, 0),
      addons: addonBreakdown,
      addonPricePerUnit: safeNumber(addonUnitPrice, 0),
      finalPricePerUnit: safeNumber(finalUnitPrice, 0),
      quantity: safeNumber(quantityNumber, 0),
      totalPrice: safeNumber(totalPrice, 0),
    };
  }, [
    baseUnitPrice,
    addonBreakdown,
    addonUnitPrice,
    finalUnitPrice,
    quantityNumber,
    totalPrice,
  ]);

  const validateBeforeSubmit = () => {
    if (!formData.firmName.trim()) {
      return "Please enter a job title.";
    }

    if (!formData.quantity || Number(formData.quantity) <= 0) {
      return "Please enter quantity.";
    }

    if (!businessProductFile) {
      return "Please upload a PDF or CDR file.";
    }

    const quantity = Number(formData.quantity || 0);

    if (quantity < minimumQuantity) {
      return `Minimum quantity is ${minimumQuantity}.`;
    }

    if (quantity % minimumQuantity !== 0) {
      return `Quantity must be a multiple of ${minimumQuantity}.`;
    }

    if (missingRequiredAddons.length > 0) {
      return `Please select all required add-ons: ${missingRequiredAddons
        .map((item) => item.label)
        .join(", ")}.`;
    }

    const total = Number(formData.totalPrice || 0);
    if (total > wallet) {
      setInsufficientBalance(true);
      return "Insufficient wallet balance.";
    }

    return "";
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        setError("");
        setSuccess("");

        if (!token || !isAuthenticated()) {
          throw new Error("Please log in again.");
        }

        const storedUser = auth?.storedUser || null;

        const email = auth?.email || storedUser?.email || null;

        const safeDistributionId =
          auth?.distributionId ||
          auth?.authUserId ||
          storedUser?.distributionId ||
          storedUser?.id ||
          storedUser?._id ||
          null;

        const [{ data: productResponse }, { data: distributionsResponse }] =
          await Promise.all([
            axios.get(`${API}/businessProducts/getBusinessProductById/${id}`),
            axios.get(`${API}/distribution/`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

        const product = productResponse?.data || productResponse;

        let distributions = [];
        if (Array.isArray(distributionsResponse)) {
          distributions = distributionsResponse;
        } else if (Array.isArray(distributionsResponse?.data)) {
          distributions = distributionsResponse.data;
        } else if (Array.isArray(distributionsResponse?.distributions)) {
          distributions = distributionsResponse.distributions;
        }

        const matchedUser =
          distributions.find(
            (item) =>
              safeDistributionId &&
              String(item?._id || item?.id) === String(safeDistributionId),
          ) ||
          distributions.find(
            (item) =>
              email &&
              String(item?.email || "").toLowerCase() ===
                String(email).toLowerCase(),
          ) ||
          storedUser;

        if (!matchedUser) {
          throw new Error("Distribution application not found.");
        }

        const normalizedUser = {
          id: matchedUser?._id || matchedUser?.id || null,
          _id: matchedUser?._id || matchedUser?.id || null,
          distributionId:
            matchedUser?._id ||
            matchedUser?.id ||
            storedUser?.distributionId ||
            null,
          email: matchedUser?.email || storedUser?.email || "",
          businessName:
            matchedUser?.businessName || storedUser?.businessName || "",
          contactPerson:
            matchedUser?.contactPerson || storedUser?.contactPerson || "",
          mobile: matchedUser?.mobile || storedUser?.mobile || "",
          wallet: Number(matchedUser?.wallet ?? storedUser?.wallet ?? 0),
          userType: matchedUser?.userType || storedUser?.userType || "",
          role: matchedUser?.role || storedUser?.role || "",
          category: matchedUser?.category || storedUser?.category || "",
          distributionType:
            matchedUser?.distributionType || storedUser?.distributionType || "",
        };

        const normalizedAddonOptions = ADDON_CONFIG.reduce((acc, addon) => {
          acc[addon.listField] = normalizeAddonOptions(
            product?.[addon.listField],
          );
          return acc;
        }, {});

        const initialSelections = createInitialSelections();
        const initialQty = Number(product?.minimumQuantity || 1);
        const initialBasePrice = getBasePriceFromProduct(
          product,
          normalizedUser,
          initialQty,
        );

        setUserDetails(normalizedUser);
        setWallet(Number(normalizedUser.wallet || 0));
        setMinimumQuantity(Number(product?.minimumQuantity || 1));
        setProductDetails(product);
        setAddonOptionsMap(normalizedAddonOptions);
        setSelectedAddons(initialSelections);

        setFormData({
          product: product?.name || "",
          deliveryTime:
            product?.expectedDeliveryTime || product?.shippingTime || "",
          firmName: "",
          quantity: String(initialQty),
          price: String(Number(initialBasePrice).toFixed(2)),
          totalPrice: String(
            (Number(initialBasePrice) * initialQty).toFixed(2),
          ),
        });

        localStorage.setItem(
          "user",
          JSON.stringify({
            id: normalizedUser.id,
            _id: normalizedUser._id,
            distributionId: normalizedUser.distributionId,
            email: normalizedUser.email,
            businessName: normalizedUser.businessName,
            contactPerson: normalizedUser.contactPerson,
            mobile: normalizedUser.mobile,
            wallet: normalizedUser.wallet,
          }),
        );

        localStorage.setItem(
          "distributionUser",
          JSON.stringify({
            id: normalizedUser.id,
            _id: normalizedUser._id,
            distributionId: normalizedUser.distributionId,
            email: normalizedUser.email,
            businessName: normalizedUser.businessName,
            contactPerson: normalizedUser.contactPerson,
            mobile: normalizedUser.mobile,
            wallet: normalizedUser.wallet,
          }),
        );
      } catch (err) {
        console.error("Initialization error:", err);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load data.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      initializeData();
    }
  }, [id, auth, token]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      price: finalUnitPrice.toFixed(2),
      totalPrice: totalPrice.toFixed(2),
    }));

    setInsufficientBalance(totalPrice > Number(wallet || 0));
  }, [finalUnitPrice, totalPrice, wallet]);

  useEffect(() => {
    return cleanupFilePreview;
  }, [cleanupFilePreview]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setError("");
    setSuccess("");

    if (id === "quantity") {
      const numValue = Math.max(0, parseInt(value || "0", 10) || 0);

      setFormData((prev) => ({
        ...prev,
        [id]: String(numValue),
      }));

      setQuantityWarning(
        numValue < minimumQuantity || numValue % minimumQuantity !== 0,
      );
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const handleAddonSelectionChange = (selectionKey, value) => {
    setError("");
    setSuccess("");
    setSelectedAddons((prev) => ({
      ...prev,
      [selectionKey]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setError("");
    setSuccess("");

    if (!file) {
      setBusinessProductFile(null);
      cleanupFilePreview();
      return;
    }

    const fileExtension = file.name.toLowerCase().split(".").pop();
    const isValidType =
      file.type === "application/pdf" || ["pdf", "cdr"].includes(fileExtension);

    if (!isValidType) {
      setError("Please upload only PDF or CDR files.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("File size must be less than 10MB.");
      e.target.value = "";
      return;
    }

    setBusinessProductFile(file);
    cleanupFilePreview();

    if (file.type === "application/pdf") {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    }
  };

  const resetForm = () => {
    const qty = Number(minimumQuantity || 1);
    const basePrice = getBasePriceFromProduct(productDetails, userDetails, qty);

    setFormData({
      product: productDetails?.name || "",
      deliveryTime:
        productDetails?.expectedDeliveryTime ||
        productDetails?.shippingTime ||
        "",
      firmName: "",
      quantity: String(qty),
      price: String(basePrice.toFixed(2)),
      totalPrice: String((qty * basePrice).toFixed(2)),
    });

    setSelectedAddons(createInitialSelections());
    setBusinessProductFile(null);
    cleanupFilePreview();
    setInsufficientBalance(false);
    setQuantityWarning(false);
    setShowDieShapeModal(false);
    setShowWarning(false);
    setError("");
    setSuccess("");

    const fileInput = document.getElementById("file");
    if (fileInput) fileInput.value = "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationMessage = validateBeforeSubmit();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setInsufficientBalance(false);
    setShowWarning(true);
  };

  const confirmAndCreate = async () => {
    if (isSubmitting) return;

    setShowWarning(false);
    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      if (!token || !isAuthenticated()) {
        throw new Error("Please log in again.");
      }

      if (!productDetails?.category) {
        throw new Error("Product category not found.");
      }

      if (
        !userDetails?._id &&
        !userDetails?.id &&
        !userDetails?.distributionId
      ) {
        throw new Error("User details not found.");
      }

      const orderAmount = Number(formData.totalPrice || 0);
      const orderDetails = new FormData();

      orderDetails.append("productId", id);
      orderDetails.append("productName", formData.product);

      orderDetails.append(
        "category",
        productDetails?.category?._id ||
          productDetails?.category?.id ||
          productDetails?.category ||
          "",
      );

      orderDetails.append("categoryName", productDetails?.category?.name || "");
      orderDetails.append(
        "userId",
        userDetails?._id ||
          userDetails?.id ||
          userDetails?.distributionId ||
          "",
      );
      orderDetails.append("userDetails", JSON.stringify(userDetails));
      orderDetails.append("totalPrice", orderAmount.toString());
      orderDetails.append("quantity", String(quantityNumber));
      orderDetails.append("firmName", formData.firmName.trim());
      orderDetails.append("orderStatus", "pending");
      orderDetails.append("orderDate", new Date().toISOString());
      orderDetails.append(
        "unitPrice",
        Number(calculationSummary.finalPricePerUnit || 0).toString(),
      );
      orderDetails.append(
        "basePricePerUnit",
        Number(calculationSummary.basePricePerUnit || 0).toString(),
      );
      orderDetails.append(
        "addonPricePerUnit",
        Number(calculationSummary.addonPricePerUnit || 0).toString(),
      );

      if (businessProductFile) {
        orderDetails.append("file", businessProductFile);
      }

      const walletHistoryData = {
        amount: orderAmount,
        remark: "Order Payment",
        receiptId: `REC-${Date.now()}`,
        date: new Date().toISOString(),
        previousBalance: wallet,
        newBalance: wallet - orderAmount,
      };
      orderDetails.append("walletHistory", JSON.stringify(walletHistoryData));

      const selectedCustomizations = ADDON_CONFIG.reduce((acc, addon) => {
        const selectedValue = selectedAddons?.[addon.selectionKey] || "";
        acc[addon.selectionKey] = selectedValue;
        acc[addon.enabledField] = Boolean(selectedValue);
        return acc;
      }, {});

      orderDetails.append(
        "customizations",
        JSON.stringify(selectedCustomizations),
      );

      const calculationData = {
        basePricePerUnit: calculationSummary.basePricePerUnit,
        addonPricePerUnit: calculationSummary.addonPricePerUnit,
        finalPricePerUnit: calculationSummary.finalPricePerUnit,
        quantity: calculationSummary.quantity,
        totalPrice: calculationSummary.totalPrice,
        addons: calculationSummary.addons.map((item) => ({
          key: item.key,
          label: item.label,
          selected: item.selected || "",
          value: item.value || "",
          pricePerUnit: item.pricePerUnit,
          totalPrice:
            safeNumber(item.pricePerUnit, 0) * calculationSummary.quantity,
        })),
      };
      orderDetails.append(
        "calculationSummary",
        JSON.stringify(calculationData),
      );

      const response = await axios.post(
        `${API}/businessOrders/createBusinessOrder`,
        orderDetails,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 201 || response.status === 200) {
        const orderId =
          response?.data?.BusinessOrder?._id ||
          response?.data?.data?._id ||
          response?.data?._id ||
          "";

        setWallet((prev) => Number(prev) - Number(orderAmount));
        setSuccess(`Order ${orderId || ""} created successfully.`);

        setTimeout(() => {
          resetForm();
          navigate("/", { replace: true });
        }, 800);
      }
    } catch (err) {
      console.error("Order creation error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Error submitting the order.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="container mx-auto flex min-h-screen items-center justify-center p-6">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading order form...</p>
        </div>
      </div>
    );
  }

  if (error && !productDetails) {
    return (
      <div className="container mx-auto min-h-screen p-6">
        <div className="mb-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
          <h3 className="font-bold">Error</h3>
          <p>{error}</p>
        </div>
        <button
          onClick={() => navigate(0)}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto min-h-screen p-4 md:p-6">
      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 rounded-t-2xl border-b border-gray-100 bg-white/95 px-5 py-4 backdrop-blur-sm">
              <h3 className="text-center text-lg font-semibold tracking-wide text-gray-800 md:text-xl">
                Consent to Proceed
              </h3>
            </div>

            <div className="flex flex-col items-center p-5 md:p-6">
              <div className="flex justify-center rounded-xl border border-gray-100 bg-gray-50 p-4 shadow-sm">
                <img
                  src={warningImage}
                  alt="Order Warning"
                  className="w-full max-w-md rounded-xl object-contain shadow-md"
                />
              </div>

              <p className="mt-4 max-w-md text-center text-sm text-gray-600">
                Please read the warning carefully before confirming your order.
              </p>

              <div className="mt-5 w-full rounded-xl border border-blue-100 bg-blue-50 p-4">
                <h4 className="mb-3 font-semibold text-blue-900">
                  Order Calculation Summary
                </h4>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-700">Base Price / Unit</span>
                    <span className="font-medium">
                      ₹{formatCurrency(calculationSummary.basePricePerUnit)}
                    </span>
                  </div>

                  {calculationSummary.addons.map((item, index) => (
                    <div
                      key={`${item.key || item.label}-${index}`}
                      className="flex justify-between gap-3"
                    >
                      <span className="text-gray-700">
                        {item.label}
                        {item.value ? ` (${item.value})` : ""}
                      </span>
                      <span className="font-medium">
                        + ₹{formatCurrency(item.pricePerUnit)} / unit
                      </span>
                    </div>
                  ))}

                  <div className="flex justify-between gap-3 border-t pt-2 font-medium">
                    <span>Add-ons Total / Unit</span>
                    <span>
                      ₹{formatCurrency(calculationSummary.addonPricePerUnit)}
                    </span>
                  </div>

                  <div className="flex justify-between gap-3 text-base font-semibold">
                    <span>Final Price / Unit</span>
                    <span>
                      ₹{formatCurrency(calculationSummary.finalPricePerUnit)}
                    </span>
                  </div>

                  <div className="flex justify-between gap-3">
                    <span>Quantity</span>
                    <span>{calculationSummary.quantity}</span>
                  </div>

                  <div className="flex justify-between gap-3 border-t pt-2 text-base font-bold text-green-700">
                    <span>Total Amount</span>
                    <span>
                      ₹{formatCurrency(calculationSummary.totalPrice)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex w-full flex-col justify-center gap-3 sm:flex-row">
                <button
                  onClick={() => setShowWarning(false)}
                  className="rounded-lg border border-gray-200 bg-gray-100 px-5 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-200"
                  type="button"
                >
                  Cancel
                </button>

                <button
                  onClick={confirmAndCreate}
                  disabled={isSubmitting}
                  className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-md transition-all duration-200 hover:bg-blue-700 hover:shadow-lg disabled:cursor-not-allowed disabled:bg-blue-400"
                  type="button"
                >
                  {isSubmitting ? "Processing..." : "Confirm & Place"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDieShapeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b bg-white px-5 py-4">
              <h3 className="text-lg font-semibold text-gray-800 md:text-xl">
                Die Shapes
              </h3>
              <button
                type="button"
                onClick={() => setShowDieShapeModal(false)}
                className="text-2xl leading-none text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="p-4 md:p-6">
              <img
                src={dieShapeGuide}
                alt="Die Shapes Guide"
                className="max-h-[70vh] w-full rounded-xl border border-gray-200 object-contain"
              />

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowDieShapeModal(false)}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <h1 className="mb-8 text-2xl font-bold md:text-3xl">Create Order</h1>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">
          {success}
        </div>
      ) : null}

      <div className="rounded-lg border bg-white p-4 shadow-lg md:p-8">
        <form
          className="grid grid-cols-1 gap-6 md:grid-cols-2"
          onSubmit={handleSubmit}
        >
          <div>
            <label
              htmlFor="product"
              className="block text-sm font-medium text-gray-700"
            >
              Product
            </label>
            <input
              id="product"
              type="text"
              value={formData.product}
              readOnly
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-gray-900 shadow-sm"
            />
          </div>

          <div>
            <label
              htmlFor="deliveryTime"
              className="block text-sm font-medium text-gray-700"
            >
              Delivery Time
            </label>
            <input
              id="deliveryTime"
              type="text"
              value={formData.deliveryTime}
              readOnly
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-gray-900 shadow-sm"
            />
          </div>

          {productDetails?.description && (
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Product Description
              </label>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="leading-relaxed text-sm text-gray-800">
                  {productDetails.description}
                </p>
              </div>
            </div>
          )}

          {(productDetails?.dimensions ||
            productDetails?.weight ||
            productDetails?.material ||
            productDetails?.category) && (
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Product Information
              </label>
              <div className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 md:grid-cols-4">
                {productDetails.category && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Category
                    </span>
                    <p className="text-sm text-gray-900">
                      {productDetails.category?.name || productDetails.category}
                    </p>
                  </div>
                )}
                {productDetails.dimensions && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Dimensions
                    </span>
                    <p className="text-sm text-gray-900">
                      {productDetails.dimensions}
                    </p>
                  </div>
                )}
                {productDetails.weight && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Weight
                    </span>
                    <p className="text-sm text-gray-900">
                      {productDetails.weight}
                    </p>
                  </div>
                )}
                {productDetails.material && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Material
                    </span>
                    <p className="text-sm text-gray-900">
                      {productDetails.material}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <label
              htmlFor="quantity"
              className="block text-sm font-medium text-gray-700"
            >
              Quantity <span className="text-red-500">*</span> (Min:{" "}
              {minimumQuantity})
            </label>
            <input
              id="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              min={minimumQuantity}
              required
              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
            {quantityWarning ? (
              <p className="mt-1 text-sm text-yellow-600">
                Quantity must be a multiple of {minimumQuantity}
              </p>
            ) : null}
          </div>

          {enabledAddons.length > 0 && (
            <div className="md:col-span-2">
              <label className="mb-3 block text-sm font-medium text-gray-700">
                SELECT FINAL PRODUCT <span className="text-red-500">*</span>
              </label>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {enabledAddons.map((addon) => {
                  const options = addonOptionsMap?.[addon.listField] || [];
                  const value = selectedAddons?.[addon.selectionKey] || "";

                  return (
                    <div key={addon.key}>
                      <div className="flex items-center justify-between gap-3">
                        <label className="block text-sm font-medium text-gray-700">
                          {addon.label} <span className="text-red-500">*</span>
                        </label>

                        {addon.key === "dieShape" && isDieShapeEnabled ? (
                          <button
                            type="button"
                            onClick={() => setShowDieShapeModal(true)}
                            className="rounded-lg bg-blue-100 px-3 py-1.5 text-xs text-blue-700 transition hover:bg-blue-200"
                          >
                            View Die Shapes
                          </button>
                        ) : null}
                      </div>

                      <select
                        value={value}
                        required
                        onChange={(e) =>
                          handleAddonSelectionChange(
                            addon.selectionKey,
                            e.target.value,
                          )
                        }
                        className="mt-1 w-full rounded-lg border p-3 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select {addon.label}</option>
                        {options.map((option) => (
                          <option key={option.name} value={option.name}>
                            {option.name}
                            {Number(option.pricePerUnit || 0) > 0
                              ? ` (+₹${formatCurrency(option.pricePerUnit)}/unit)`
                              : ""}
                          </option>
                        ))}
                      </select>

                      {value ? (
                        <p className="mt-1 text-xs text-blue-700">
                          +₹
                          {formatCurrency(
                            getSelectedAddonPrice(options, value),
                          )}{" "}
                          / unit
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label
              htmlFor="price"
              className="block text-sm font-medium text-gray-700"
            >
              Price Per Unit
            </label>
            <input
              id="price"
              type="text"
              value={formData.price}
              readOnly
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-gray-900 shadow-sm"
            />
          </div>

          <div>
            <label
              htmlFor="totalPrice"
              className="block text-sm font-medium text-gray-700"
            >
              Total Price
            </label>
            <input
              id="totalPrice"
              type="text"
              value={formData.totalPrice}
              readOnly
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-gray-900 shadow-sm"
            />
          </div>

          <div>
            <label
              htmlFor="firmName"
              className="block text-sm font-medium text-gray-700"
            >
              Job Title <span className="text-red-500">*</span>
            </label>
            <input
              id="firmName"
              type="text"
              value={formData.firmName}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <h3 className="mb-3 text-base font-semibold text-blue-900">
                Pricing Breakdown
              </h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-700">Base Price / Unit</span>
                  <span className="font-medium">
                    ₹{formatCurrency(calculationSummary.basePricePerUnit)}
                  </span>
                </div>

                {calculationSummary.addons.map((item, index) => (
                  <div
                    key={`${item.key || item.label}-${index}`}
                    className="flex justify-between gap-3"
                  >
                    <span className="text-gray-700">
                      {item.label}
                      {item.value ? ` (${item.value})` : ""}
                    </span>
                    <span className="font-medium text-blue-700">
                      + ₹{formatCurrency(item.pricePerUnit)} / unit
                    </span>
                  </div>
                ))}

                <div className="flex justify-between gap-3 border-t border-blue-200 pt-2">
                  <span className="font-medium text-gray-700">
                    Add-ons Total / Unit
                  </span>
                  <span className="font-semibold text-blue-800">
                    ₹{formatCurrency(calculationSummary.addonPricePerUnit)}
                  </span>
                </div>

                <div className="flex justify-between gap-3 text-base">
                  <span className="font-semibold text-gray-900">
                    Final Price / Unit
                  </span>
                  <span className="font-bold text-gray-900">
                    ₹{formatCurrency(calculationSummary.finalPricePerUnit)}
                  </span>
                </div>

                <div className="flex justify-between gap-3">
                  <span className="text-gray-700">Quantity</span>
                  <span className="font-medium">
                    {calculationSummary.quantity}
                  </span>
                </div>

                <div className="flex justify-between gap-3 border-t border-blue-200 pt-2 text-base">
                  <span className="font-bold text-green-700">Total Amount</span>
                  <span className="font-bold text-green-700">
                    ₹{formatCurrency(calculationSummary.totalPrice)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <label
              htmlFor="file"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Upload PDF/CDR File <span className="text-red-500">*</span>
            </label>

            <div className="space-y-4">
              <div className="flex w-full items-center justify-center">
                <label
                  htmlFor="file"
                  className="flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:bg-gray-100"
                >
                  <div className="flex flex-col items-center justify-center pb-6 pt-5">
                    <svg
                      className="mb-4 h-8 w-8 text-gray-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF or CDR files only (Max 10MB)
                    </p>
                  </div>
                  <input
                    id="file"
                    type="file"
                    accept=".pdf,.cdr"
                    onChange={handleFileChange}
                    className="hidden"
                    required
                  />
                </label>
              </div>

              {businessProductFile ? (
                <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3">
                  <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                    <div className="flex flex-1 items-center gap-3">
                      <div className="rounded-md bg-blue-100 p-1.5">
                        <svg
                          className={`h-5 w-5 ${
                            businessProductFile.type === "application/pdf"
                              ? "text-red-600"
                              : "text-blue-600"
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M4 18h12V6h-4V2H4v16zm8-14v4h4l-4-4z" />
                        </svg>
                      </div>
                      <div className="text-sm">
                        <p className="max-w-[150px] truncate font-medium text-gray-800">
                          {businessProductFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(businessProductFile.size / 1024 / 1024).toFixed(2)}{" "}
                          MB
                        </p>
                      </div>
                    </div>

                    {filePreviewUrl &&
                    businessProductFile?.type === "application/pdf" ? (
                      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md">
                        <div className="flex flex-col gap-2 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-gray-800 md:text-base">
                              PDF Preview
                            </h4>
                            <p className="truncate text-xs text-gray-500">
                              {businessProductFile?.name || "Uploaded file"}
                            </p>
                          </div>

                          <a
                            href={filePreviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-600 transition hover:bg-blue-100"
                          >
                            Open Full View
                          </a>
                        </div>

                        <div className="p-3 md:p-4">
                          <div className="h-[320px] w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-inner sm:h-[420px] md:h-[520px]">
                            <iframe
                              src={`${filePreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                              className="h-full w-full border-0"
                              title="PDF Preview"
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => {
                        setBusinessProductFile(null);
                        cleanupFilePreview();
                        const el = document.getElementById("file");
                        if (el) el.value = "";
                      }}
                      className="self-start text-red-600 hover:text-red-800 md:self-center"
                      title="Remove File"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  {filePreviewUrl &&
                  businessProductFile?.type === "application/pdf" ? (
                    <p className="mt-1 text-center text-[11px] text-gray-500">
                      Preview only – full file will be uploaded.
                    </p>
                  ) : null}
                </div>
              ) : null}

              {businessProductFile &&
              businessProductFile.name.toLowerCase().endsWith(".cdr") ? (
                <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                  <div className="flex items-center">
                    <svg
                      className="mr-2 h-5 w-5 text-yellow-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-yellow-800">
                      CDR file uploaded successfully. Preview not available for
                      CDR files.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {insufficientBalance ? (
            <div className="md:col-span-2">
              <div className="rounded-lg border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                <div className="flex items-center">
                  <svg
                    className="mr-2 h-5 w-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">Insufficient wallet balance</p>
                    <p className="text-sm">
                      Current balance: ₹{formatCurrency(wallet)} | Required: ₹
                      {formatCurrency(formData.totalPrice)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="md:col-span-2">
            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center">
                  <svg
                    className="mr-2 h-5 w-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                  </svg>
                  <span className="font-medium">
                    Current Wallet Balance: ₹{formatCurrency(wallet)}
                  </span>
                </div>
                {formData.totalPrice ? (
                  <span className="text-sm">
                    Remaining after order: ₹
                    {formatCurrency(wallet - Number(formData.totalPrice || 0))}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 md:col-span-2">
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg bg-gray-600 px-6 py-3 font-medium text-white transition-colors hover:bg-gray-700"
            >
              Reset Form
            </button>
            <button
              type="submit"
              disabled={
                isSubmitting ||
                insufficientBalance ||
                missingRequiredAddons.length > 0
              }
              className={`rounded-lg px-6 py-3 font-medium text-white transition-colors ${
                isSubmitting ||
                insufficientBalance ||
                missingRequiredAddons.length > 0
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg
                    className="-ml-1 mr-3 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting...
                </div>
              ) : (
                "Submit Order"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrder;
